import React from "react";
import { Link } from "react-router-dom";
import { FiTwitter, FiInstagram, FiMail, FiMapPin } from "react-icons/fi";
import { FaGoogle } from "react-icons/fa";
import logo from "../assets/logo-Photoroom.png";

const Footer = () => {
  const currentYear = new Date().getFullYear();
  
  return (
    <footer className="relative pt-16 pb-8 bg-[#1C1F2E] overflow-hidden">
      {/* Enhanced Background Elements */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-teal-500/5 rounded-full filter blur-[120px]"></div>
        <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-purple-500/5 rounded-full filter blur-[120px]"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 md:gap-8 mb-12">
          {/* Brand Column */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6 group">
              <div className="h-12 flex items-center transition-transform duration-300 group-hover:scale-105">
                <img
                  src={logo}
                  alt="Viszapp Logo"
                  className="h-full object-contain"
                  style={{ maxWidth: "180px" }}
                />
              </div>
            </Link>
            <p className="text-gray-400 mb-6 leading-relaxed">
              Simplifying visa applications with expert guidance and
              personalized support for your journey.
            </p>
            {/* Social Links */}
            <div className="flex gap-3">
              <a
                href="https://twitter.com/viszapp"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 bg-white/5 rounded-lg hover:bg-teal-500/10 text-gray-400 
                         hover:text-teal-400 border border-transparent hover:border-teal-500/30
                         transition-all duration-300"
                aria-label="Follow us on Twitter"
              >
                <FiTwitter size={20} />
              </a>
              <a
                href="https://instagram.com/viszapp"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 bg-white/5 rounded-lg hover:bg-teal-500/10 text-gray-400 
                         hover:text-teal-400 border border-transparent hover:border-teal-500/30
                         transition-all duration-300"
                aria-label="Follow us on Instagram"
              >
                <FiInstagram size={20} />
              </a>
              <a
                href="https://www.google.com/search?q=Viszapp"
                target="_blank"
                rel="noopener noreferrer"
                className="p-2.5 bg-white/5 rounded-lg hover:bg-teal-500/10 text-gray-400 
                         hover:text-teal-400 border border-transparent hover:border-teal-500/30
                         transition-all duration-300"
                aria-label="Find us on Google"
              >
                <FaGoogle size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Quick Links
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-teal-400"></span>
            </h3>
            <ul className="list-none p-0 space-y-3.5">
              {[
                { name: "Home", path: "/" },
                { name: "About Founders", path: "/know-us" },
                { name: "How it Works", path: "/how-it-works" },
                { name: "Blogs", path: "/blogs" },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors duration-300 
                             hover:translate-x-1 inline-block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Legal
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-teal-400"></span>
            </h3>
            <ul className="list-none p-0 space-y-3.5">
              {[
                { name: "Privacy Policy", path: "/privacy" },
                { name: "Terms of Service", path: "/terms" },
                { name: "Service Policies", path: "/service-policies" },
              ].map((item) => (
                <li key={item.name}>
                  <Link
                    to={item.path}
                    className="text-gray-400 hover:text-teal-400 transition-colors duration-300 
                             hover:translate-x-1 inline-block"
                  >
                    {item.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold text-lg mb-6 relative inline-block">
              Contact Us
              <span className="absolute bottom-0 left-0 w-12 h-0.5 bg-teal-400"></span>
            </h3>
            <ul className="list-none p-0 space-y-3.5">
              <li>
                <a
                  href="mailto:support@viszapp.com"
                  className="flex items-start gap-3 text-gray-400 hover:text-teal-400 
                           transition-colors duration-300 group"
                >
                  <FiMail className="mt-1 flex-shrink-0 group-hover:scale-110 transition-transform" />
                  <span>support@viszapp.com</span>
                </a>
              </li>
              <li>
                <div className="flex items-start gap-3 text-gray-400">
                  <FiMapPin className="mt-1 flex-shrink-0" />
                  <span> Survey Number 43, 9, Tulsi Nagar Rd, Tulsi Nagar, Nipania, Indore, Madhya Pradesh, 452010</span>
                </div>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="pt-8 border-t border-gray-700/50">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-400 text-sm">
              © {currentYear} Viszapp. All rights reserved.
            </p>
            <p className="text-gray-400 text-sm flex items-center gap-2">
              Made with{" "}
              <span className="text-red-500 animate-pulse">♥</span>{" "}
              in India 🇮🇳
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
